import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import LeadForm from '../components/LeadForm.jsx';
import Badge, { PosBadge, OpenStatusBadge } from '../components/Badge.jsx';
import QuoteDialog from '../components/QuoteDialog.jsx';
import QuotesTab from '../components/QuotesTab.jsx';
import LeadSubscriptionsTab from '../components/LeadSubscriptionsTab.jsx';
import DateInput from '../components/DateInput.jsx';
import ContactsSection from '../components/ContactsSection.jsx';
import SendEmailDialog from '../components/SendEmailDialog.jsx';
import PackageSelector from '../components/PackageSelector.jsx';
import OutreachStageDot from '../components/OutreachStageDot.jsx';
import CopyMeetingInfoButton from '../components/CopyMeetingInfoButton.jsx';
import EmailOpenBadge from '../components/EmailOpenBadge.jsx';
import CallsTab from '../components/CallsTab.jsx';
import EmailsTab from '../components/EmailsTab.jsx';
import { useAuth } from '../context/AuthContext.jsx';

const TABS = [
  { id: 'details', label: 'Details' },
  { id: 'notes', label: 'Notes' },
  { id: 'calls', label: 'Calls' },
  { id: 'emails', label: 'Emails' },
  { id: 'quotes', label: 'Quotes' },
  { id: 'documents', label: 'Documents' },
  { id: 'subscriptions', label: 'Subscriptions' }
];

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function formatTime(value) {
  if (!value) return '';
  const s = String(value);
  const match = s.match(/^(\d{2}):(\d{2})/);
  return match ? `${match[1]}:${match[2]}` : s;
}

function noteTone(type) {
  switch (type) {
    case 'Phone': return 'blue';
    case 'Email': return 'purple';
    case 'Meeting': return 'green';
    case 'Demo': return 'orange';
    default: return 'grey';
  }
}

function probabilityTone(p) {
  if (p == null) return 'grey';
  if (p >= 70) return 'green';
  if (p >= 30) return 'amber';
  return 'red';
}

function displayName(lead) {
  if (!lead) return 'Lead';
  if (lead.company) return lead.company;
  const parts = [lead.first_name, lead.last_name].filter(Boolean);
  return parts.join(' ') || `Lead #${lead.id}`;
}

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState('details');
  const [saveMessage, setSaveMessage] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [claiming, setClaiming] = useState(false);
  // Separate state for the destructive delete flow so it can't get
  // tangled up with the Convert-to-Customer confirm dialog.
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  async function handleDelete() {
    setDeleting(true);
    setDeleteError(null);
    try {
      await axios.delete(`/api/leads/${id}`);
      navigate('/leads');
    } catch (err) {
      setDeleteError(err.response?.data?.error || err.message);
      setDeleting(false);
    }
  }

  // Self-claim. Allow-re-steal model (Dave 2026-06-09): no confirmation
  // prompt, just one click. If another rep already owns it, this
  // silently overwrites. Sets the Owner picker on the LeadForm to the
  // current user as a side effect (via the reload).
  async function handleClaim() {
    setClaiming(true);
    try {
      await axios.post(`/api/leads/${id}/claim`);
      await load();
    } catch (err) {
      window.alert('Claim failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setClaiming(false);
    }
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/leads/${id}`);
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  async function handleUpdate(form) {
    await axios.put(`/api/leads/${id}`, form);
    setSaveMessage('Saved.');
    await load();
    setTimeout(() => setSaveMessage(null), 2500);
  }

  async function handleConvert() {
    setConverting(true);
    setConvertError(null);
    try {
      await axios.post(`/api/leads/${id}/convert`);
      navigate(`/customers/${id}`);
    } catch (err) {
      setConvertError(err.response?.data?.error || err.message);
      setConverting(false);
    }
  }

  if (loading && !data) {
    return <section className="page"><p>Loading…</p></section>;
  }

  if (error && !data) {
    return (
      <section className="page">
        <div className="page-header">
          <h2>Lead</h2>
          <Link to="/leads" className="btn-secondary">← Back to Sales Leads</Link>
        </div>
        <p className="error">{error}</p>
      </section>
    );
  }

  const lead = data.lead;
  const location = [lead.city, lead.province_state].filter(Boolean).join(', ');
  const contactName = [lead.first_name, lead.last_name].filter(Boolean).join(' ');

  return (
    <section className="page">
      <div className="page-header">
        <Link to="/leads" className="btn-secondary">← Back to Sales Leads</Link>
      </div>

      <div className="record-header">
        <div className="record-title" style={{ flex: 1 }}>
          <h2>
            <OutreachStageDot stage={lead.outreach_stage} size={12} />
            {displayName(lead)}
          </h2>
          <div className="record-meta">
            {lead.site_number && <span className="cell-mono">Site #{lead.site_number}</span>}
            {lead.pos_type && <><span className="sep" /><PosBadge value={lead.pos_type} /></>}
            {lead.close_probability != null && (
              <><span className="sep" /><Badge tone={probabilityTone(lead.close_probability)}>{lead.close_probability}% close</Badge></>
            )}
            {lead.expected_close_date && (
              <><span className="sep" />Expected close {formatDate(lead.expected_close_date)}</>
            )}
            {location && <><span className="sep" />{location}</>}
            {contactName && <><span className="sep" />{contactName}</>}
            {lead.not_interested && <><span className="sep" /><Badge tone="grey">Not Interested</Badge></>}
            {/* Owner chip -- mirrors the Customer page header. Muted
                "Unclaimed" for unowned leads so reps can spot pickable
                rows from the meta line. */}
            <span className="sep" />
            {lead.owner_name ? (
              <span>Owner: <strong>{lead.owner_name}</strong></span>
            ) : (
              <span style={{ color: 'var(--color-text-faint)' }}>Unclaimed</span>
            )}
          </div>
        </div>
        <div className="record-actions">
          {/* Copy Meeting Info -- one-click clipboard copy of the 3-line
              meeting block (site/POS/location, contact, host). Pastes
              cleanly into Teams/Zoom/agenda fields. Dave 2026-06-30. */}
          <CopyMeetingInfoButton record={lead} user={currentUser} kind="lead" contactId={id} onLogged={load} />
          <PackageSelector
            contactId={lead.id}
            recordType="lead"
            value={lead.package}
            onApplied={load}
          />
          {/* Claim button -- always one click, no confirmation. Hidden
              when the current user already owns the lead. */}
          {lead.owner_id !== currentUser?.id && (
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClaim}
              disabled={converting || deleting || claiming}
              title={lead.owner_id
                ? `Reassign from ${lead.owner_name || 'current owner'} to you`
                : 'Take ownership of this lead'}
            >
              {claiming ? 'Claiming…' : (lead.owner_id ? 'Claim from ' + (lead.owner_name?.split(' ')[0] || 'owner') : 'Claim')}
            </button>
          )}
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setSendEmailOpen(true)}
            disabled={converting || deleting}
            title="Send a templated email to this lead"
          >
            Send Email
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setQuoteOpen(true)}
            disabled={converting || deleting}
          >
            Generate Quote
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={() => setDeleteConfirmOpen(true)}
            disabled={converting || deleting}
            title="Delete this lead and all related records"
          >
            Delete
          </button>
          {!confirming && (
            <button
              type="button"
              className="btn-primary"
              onClick={() => setConfirming(true)}
              disabled={converting || deleting}
            >
              Convert to Customer →
            </button>
          )}
        </div>
      </div>

      <QuoteDialog
        open={quoteOpen}
        record={{
          ...lead,
          // The lead query aliases lead_details.pos_type as pos_type already
          // and the site_number is exposed via the same key — pass directly.
        }}
        onClose={() => setQuoteOpen(false)}
      />

      <SendEmailDialog
        open={sendEmailOpen}
        record={lead}
        onClose={() => setSendEmailOpen(false)}
        onSent={() => { load(); }}
      />

      {/* Destructive-delete confirmation. Plain modal (no fancy form
          state) since the only action is yes/no. ESC and overlay-click
          dismiss while not deleting; both buttons disable mid-request. */}
      {deleteConfirmOpen && (
        <div
          className="modal-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !deleting) setDeleteConfirmOpen(false);
          }}
        >
          <div className="modal-card modal-narrow" role="dialog" aria-modal="true" aria-label="Delete lead">
            <header className="modal-head">
              <h3>Delete Lead</h3>
              <button
                type="button"
                className="modal-close"
                onClick={() => setDeleteConfirmOpen(false)}
                disabled={deleting}
                aria-label="Close"
              >×</button>
            </header>
            <div className="modal-body">
              <p>Are you sure you want to delete this lead? This cannot be undone.</p>
              <p className="modal-sub">
                Deleting <strong>{displayName(lead)}</strong> will remove the
                contact record plus all related notes, contacts, documents,
                and subscriptions.
              </p>
              {deleteError && <p className="error">{deleteError}</p>}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setDeleteConfirmOpen(false)}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-danger"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting…' : 'Delete Lead'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirming && (
        <div className="convert-confirm">
          <div className="convert-confirm-head">
            <strong>Convert this lead to a Customer?</strong>
            <p>
              The record will move into Customers, a new Site record will be created from the lead's
              site number and address, and a Bulloch configuration request will be flagged as
              <strong> Pending</strong>. Notes and contact history are preserved.
            </p>
          </div>
          {convertError && <p className="error">{convertError}</p>}
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => setConfirming(false)} disabled={converting}>
              Cancel
            </button>
            <button type="button" className="btn-primary" onClick={handleConvert} disabled={converting}>
              {converting ? 'Converting…' : 'Confirm Conversion'}
            </button>
          </div>
        </div>
      )}

      <div className="tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`tab${active === t.id ? ' tab-active' : ''}`}
            onClick={() => setActive(t.id)}
          >
            {t.label}
            {t.id === 'notes' && data.notes.length > 0 && <span className="tab-count">{data.notes.length}</span>}
            {t.id === 'quotes' && data.documents && data.documents.filter((d) => (d.type || '').toLowerCase() === 'quote').length > 0 && (
              <span className="tab-count">{data.documents.filter((d) => (d.type || '').toLowerCase() === 'quote').length}</span>
            )}
            {t.id === 'documents' && data.documents && data.documents.length > 0 && (
              <span className="tab-count">{data.documents.length}</span>
            )}
          </button>
        ))}
      </div>

      <div className="tab-panel">
        {saveMessage && <p className="save-message">{saveMessage}</p>}

        {active === 'details' && (
          <>
            <LeadForm
              key={lead.id}
              initial={lead}
              onSubmit={handleUpdate}
              submitLabel="Save Changes"
            />
            {/* Contacts section sits inline below the lead form — same
                Mission Control layout as the customer detail page so
                the two surfaces stay visually consistent. */}
            <ContactsSection
              contactId={lead.id}
              items={data.sub_contacts || []}
              onChanged={load}
            />
          </>
        )}

        {active === 'notes' && (
          <NotesTab leadId={id} notes={data.notes} onAdded={load} />
        )}

        {active === 'calls' && <CallsTab contactId={lead.id} />}
        {active === 'emails' && <EmailsTab contactId={lead.id} />}

        {active === 'quotes' && (
          <QuotesTab items={data.documents || []} onChanged={load} />
        )}
        {active === 'documents' && (
          <DocumentsTab items={data.documents || []} />
        )}

        {active === 'subscriptions' && (
          <LeadSubscriptionsTab
            leadId={lead.id}
            subscription={data.subscription}
            onSaved={load}
          />
        )}
      </div>
    </section>
  );
}

function formatDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString();
}

function DocumentsTab({ items }) {
  if (items.length === 0) return <p className="empty-tab">No documents generated yet.</p>;
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>Type</th><th>File</th><th>Created</th><th>Sent</th>
            <th>Open Status</th><th>First Opened</th>
          </tr>
        </thead>
        <tbody>
          {items.map((d) => (
            <tr key={d.id}>
              <td>{d.type || ''}</td>
              <td className="cell-mono">{d.file_path || ''}</td>
              <td className="cell-mono">{formatDateTime(d.created_at)}</td>
              <td className="cell-mono">{formatDateTime(d.sent_at)}</td>
              <td><OpenStatusBadge document={d} /></td>
              <td className="cell-mono">{formatDateTime(d.opened_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NotesTab({ leadId, notes, onAdded }) {
  const initialForm = () => ({
    note_date: new Date().toISOString().slice(0, 10),
    type: 'Phone',
    text: ''
  });
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (!form.text.trim()) {
      setError('Note text is required.');
      return;
    }
    setSaving(true);
    try {
      await axios.post('/api/notes', {
        contact_id: Number(leadId),
        note_date: form.note_date,
        type: form.type,
        text: form.text
      });
      setForm(initialForm());
      await onAdded();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="notes-panel">
      <form onSubmit={handleSubmit} className="note-form">
        <h3>Add Note</h3>
        {error && <p className="error">{error}</p>}
        <div className="note-form-row">
          <label>
            <span>Type</span>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              <option>Phone</option>
              <option>Email</option>
              <option>Meeting</option>
              <option>Demo</option>
              <option>Other</option>
            </select>
          </label>
          <label>
            <span>Date</span>
            <DateInput value={form.note_date} onChange={(e) => setForm({ ...form, note_date: e.target.value })} />
          </label>
        </div>
        <textarea
          rows={3}
          placeholder="Note text…"
          value={form.text}
          onChange={(e) => setForm({ ...form, text: e.target.value })}
        />
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Note'}
          </button>
        </div>
      </form>

      <h3>History ({notes.length})</h3>
      {notes.length === 0 && <p className="empty-tab">No notes yet.</p>}
      <ul className="note-list">
        {notes.map((n) => {
          const typeClass = `note-type-${(n.type || 'other').toLowerCase()}`;
          return (
            <li key={n.id} className={`note-item ${typeClass}`}>
              <div className="note-meta">
                <Badge tone={noteTone(n.type)}>{n.type || 'Other'}</Badge>
                <span>{formatDate(n.note_date)}{n.note_time ? ` · ${formatTime(n.note_time)}` : ''}</span>
                {n.user_name && <span>· {n.user_name}</span>}
                <EmailOpenBadge note={n} />
              </div>
              <div className="note-text">{n.text}</div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
